<template>
  <div class="kind-upload-list">
    <!-- ファイル投入: ドロップゾーン + フォルダ選択 -->
    <div
      class="upload-dropzone mb-2"
      :class="{ 'is-dragover': dragActive, 'disabled': busy }"
      role="button"
      tabindex="0"
      @click="onDropzoneClick"
      @keydown.enter.prevent="openFileDialog"
      @keydown.space.prevent="openFileDialog"
      @dragenter.prevent.stop="onDragActive(true)"
      @dragover.prevent.stop="onDragActive(true)"
      @dragleave.prevent.stop="onDragActive(false)"
      @dragend.prevent.stop="onDragActive(false)"
      @drop.prevent.stop="onDrop"
    >
      <div class="upload-dropzone-inner text-center py-4 px-3">
        <i class="fa-solid fa-cloud-arrow-up fa-2x text-muted mb-2"></i>
        <p class="mb-2 text-muted">{{ isMobile ? __('video.upload_dropzone_hint_mobile') : __('video.upload_dropzone_hint') }}</p>
        <button type="button" class="btn btn-outline-primary btn-sm" :disabled="busy" @click.stop="openFileDialog">
          <i class="fa-solid fa-folder-open mr-1"></i>{{ __('video.upload_file_select_button') }}
        </button>
        <button type="button" class="btn btn-outline-primary btn-sm ml-2" :disabled="busy" @click.stop="openDirDialog">
          <i class="fa-solid fa-folder-tree mr-1"></i>{{ __('video.folder_upload_select_button') }}
        </button>
      </div>
    </div>
    <small class="form-text text-muted mb-3">{{ __('video.upload_multi_file_help') }}</small>
    <input ref="fileInput" type="file" multiple class="d-none" data-testid="upload-kind-file-input" @change="onFileInputChange" />
    <input ref="dirInput" type="file" webkitdirectory multiple class="d-none" data-testid="upload-dir-input" @change="onDirInputChange" />

    <div v-if="junkExcludedCount > 0" class="alert alert-info small py-2 px-3 mb-2">
      <div>{{ junkExcludedText }}</div>
      <ul class="mb-0 mt-1 pl-4" style="max-height: 8rem; overflow-y: auto;">
        <li v-for="path in junkExcludedPaths" :key="path" class="text-break">{{ path }}</li>
      </ul>
    </div>

    <!-- 登録失敗サマリ -->
    <div v-if="registerFailedCount > 0" class="alert alert-danger small py-2 px-3 mb-2">
      {{ failedSummaryText }}
    </div>

    <!-- 登録処理中の進捗 -->
    <div v-if="registering" class="alert alert-info small py-2 px-3 mb-2">
      <i class="fa-solid fa-spinner fa-spin mr-1"></i>{{ registeringProgressText }}
    </div>

    <!-- 全件数 -->
    <div v-if="items.length > 0" class="small text-muted mb-2">{{ totalCountText }}</div>

    <!-- ストレージ一括設定 -->
    <div v-if="items.length > 0" class="d-flex flex-wrap align-items-center mb-2" style="gap: 0.25rem 0.5rem;">
      <span class="small text-muted" style="white-space: nowrap;">{{ __('video.bulk_storage_label') }}</span>
      <div class="d-flex" style="gap: 0.5rem; flex-shrink: 0;">
        <button type="button" class="btn btn-sm btn-outline-secondary" style="white-space: nowrap;" :disabled="busy" @click="bulkStorage(storageHotId)">
          {{ __('video.bulk_storage_hot') }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" style="white-space: nowrap;" :disabled="busy" @click="bulkStorage(storageColdId)">
          {{ __('video.bulk_storage_cold') }}
        </button>
      </div>
    </div>

    <!-- フォルダ単位セクション -->
    <div v-if="folderItems.length > 0" class="mb-3">
      <h6 class="small font-weight-bold text-muted mb-2">{{ __('video.folder_upload_section_title') }}</h6>
      <kind-upload-folder-group
        v-for="node in folderTree"
        :key="node.path"
        :node="node"
        :depth="0"
        :busy="busy"
        :storage-hot-id="storageHotId"
        :storage-cold-id="storageColdId"
        :cold-min-bytes="coldMinBytes"
        :kind-labels="kindLabelMap"
        :folder-existence-map="folderExistenceMap"
        @remove-item="removeItem"
        @delete-source-change="onDeleteSourceChange"
      />
    </div>

    <!-- ファイル単位セクション -->
    <div v-if="normalItems.length > 0" class="mb-3">
      <h6 class="small font-weight-bold text-muted mb-2">{{ __('video.normal_upload_section_title') }}</h6>
      <div v-for="(item, idx) in normalItems" :key="item._uuid" class="kind-upload-item card mb-3 shadow-sm">
        <div class="card-header bg-transparent d-flex align-items-center py-2 px-3 flex-wrap">
          <div style="width: 220px;" class="mr-3">
            <v-select
              :options="kindOptions"
              label="label"
              :reduce="(opt) => opt"
              :searchable="false"
              :clearable="false"
              :disabled="busy"
              v-model="item._kindSelection"
              :placeholder="__('video.kind_placeholder')"
              @option:selected="onKindSelected(item, $event)"
            />
          </div>
          <div class="d-flex align-items-center mr-3">
            <div class="custom-control custom-radio custom-control-inline">
              <input
                class="custom-control-input"
                type="radio"
                :id="'storage_hot_' + item._uuid"
                :value="storageHotId"
                :disabled="busy || item.delete_source"
                v-model.number="item.storage_id"
              />
              <label
                class="custom-control-label small"
                :for="'storage_hot_' + item._uuid"
                :class="{ 'text-muted': item.delete_source }"
              >{{ __('video.hot') }}</label>
            </div>
            <div class="custom-control custom-radio custom-control-inline">
              <input
                class="custom-control-input"
                type="radio"
                :id="'storage_cold_' + item._uuid"
                :value="storageColdId"
                v-model.number="item.storage_id"
                :disabled="busy || isColdDisabled(item) || item.delete_source"
              />
              <label
                class="custom-control-label small"
                :for="'storage_cold_' + item._uuid"
                :class="{ 'text-muted': isColdDisabled(item) || item.delete_source }"
              >{{ __('video.cold') }}</label>
            </div>
          </div>

          <span class="small mr-2" :class="statusClass(item.status)">
            <i :class="statusIcon(item.status)" class="mr-1"></i>{{ statusLabel(item.status) }}
            <template v-if="item.status === 'uploading'"> {{ item.percent }}%</template>
          </span>

          <span v-if="item.register_status" class="small mr-2" :class="registerStatusClass(item.register_status)">
            <i :class="registerStatusIcon(item.register_status)" class="mr-1"></i>{{ registerStatusLabel(item.register_status) }}
          </span>

          <button
            v-if="(item.status === 'pending' || item.status === 'failed' || item.register_status === 'register_failed') && !busy"
            type="button"
            class="btn btn-link text-danger p-0 ml-auto"
            @click="removeItem(item)"
          >
            <i class="fa-solid fa-trash-can"></i>
          </button>
        </div>

        <div class="card-body px-3 py-2">
          <div v-if="item.kind === 'others'" class="mb-2 p-2 border rounded">
            <label class="small font-weight-bold mb-1">{{ __('video.kind_label') }}</label>
            <input
              type="text"
              class="form-control form-control-sm"
              v-model="item.kind_label"
              :maxlength="255"
              :disabled="busy"
              :placeholder="__('video.kind_label')"
            />
            <small class="form-text text-muted">{{ __('video.kind_label_help') }}</small>
          </div>

          <div v-if="isSourceDeleteEligible(item)" class="mb-2 p-2 border rounded">
            <div class="custom-control custom-checkbox">
              <input
                type="checkbox"
                class="custom-control-input"
                :id="'delete_source_' + item._uuid"
                v-model="item.delete_source"
                :disabled="busy"
                @change="onDeleteSourceChange(item)"
              />
              <label class="custom-control-label small" :for="'delete_source_' + item._uuid">{{ __('video.source_delete_upload_label') }}</label>
            </div>
            <small class="form-text text-muted mb-0">{{ __('video.source_delete_upload_warning') }}</small>
          </div>

          <div class="small">
            <i class="fa-regular fa-file mr-1 text-muted"></i>
            <span class="font-weight-bold">{{ item.original_name }}</span>
            <span class="text-muted ml-2">{{ formatSize(item.filesize) }}</span>
          </div>

          <div v-if="item.status === 'uploading'" class="progress mt-2" style="height: 6px;">
            <div
              class="progress-bar"
              role="progressbar"
              :style="{ width: item.percent + '%' }"
              :aria-valuenow="item.percent"
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>

          <div v-if="item.status === 'failed' && item.error" class="text-danger small mt-2">{{ item.error }}</div>
          <div v-if="item.register_status === 'register_failed' && item.register_error" class="text-danger small mt-2">{{ item.register_error }}</div>

          <!--
            以下の hidden input は送信には使わない(登録は JSON 分割送信 1 本に統一済み)。
            qa/e2e/bootstrap/create-fixtures.mjs がアップロード完了(filename 反映済み)の
            待ち合わせに参照しているため、削除せず残している。
          -->
          <input type="hidden" :name="'uploads[' + idx + '][kind]'" :value="item.kind || ''" />
          <input type="hidden" :name="'uploads[' + idx + '][kind_label]'" :value="item.kind_label || ''" />
          <input type="hidden" :name="'uploads[' + idx + '][filename]'" :value="item.filename || ''" />
          <input type="hidden" :name="'uploads[' + idx + '][filesize]'" :value="item.filesize || 0" />
          <input type="hidden" :name="'uploads[' + idx + '][original_name]'" :value="item.original_name || ''" />
          <input type="hidden" :name="'uploads[' + idx + '][source_updated_at]'" :value="item.source_updated_at || ''" />
          <input type="hidden" :name="'uploads[' + idx + '][storage_id]'" :value="item.storage_id" />
          <input type="hidden" :name="'uploads[' + idx + '][delete_source]'" :value="item.delete_source ? '1' : '0'" />
          <input type="hidden" :name="'uploads[' + idx + '][upload_expires_at]'" :value="item.upload_expires_at || ''" />
          <input type="hidden" :name="'uploads[' + idx + '][upload_token]'" :value="item.upload_token || ''" />
        </div>
      </div>
    </div>

    <!-- 全件数 -->
    <div v-if="items.length > 0" class="small text-muted mb-2">{{ totalCountText }}</div>

    <!-- アップロード操作 -->
    <div v-if="items.length > 0" class="mb-4">
      <button
        type="button"
        class="btn btn-primary"
        data-testid="upload-kind-start"
        :disabled="busy || !hasPending"
        @click="uploadAll('pending')"
      >
        <i class="fa-solid fa-upload mr-1"></i>{{ __('video.upload_action_button') }}
      </button>
      <button
        v-if="hasFailed && !busy"
        type="button"
        class="btn btn-warning ml-2"
        data-testid="upload-kind-retry"
        @click="uploadAll('failed')"
      >
        <i class="fa-solid fa-rotate-right mr-1"></i>{{ __('video.upload_retry_failed') }}
      </button>
      <span v-if="uploadProgressText" class="ml-2 small text-muted">{{ uploadProgressText }}</span>
      <span v-if="uploadCompletionText" class="ml-2 text-danger small font-weight-bold">
        {{ uploadCompletionText }}
      </span>
      <span v-if="allUploaded" class="ml-2 text-success small font-weight-bold">
        <i class="fa-solid fa-circle-check mr-1"></i>{{ __('video.upload_all_done') }}
      </span>
    </div>

    <!-- 登録していないまま画面を離れるときの確認 -->
    <Teleport to="body">
      <div
        id="uploadLeaveModal"
        ref="leaveModal"
        class="modal fade"
        tabindex="-1"
        data-testid="upload-leave-dialog"
      >
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" data-testid="upload-leave-title">{{ __('video.upload_leave_title') }}</h5>
              <button type="button" class="close" data-dismiss="modal"><span>&times;</span></button>
            </div>
            <div class="modal-body">
              <p class="mb-0" data-testid="upload-leave-body">{{ __('video.upload_leave_body') }}</p>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-dismiss="modal" data-testid="upload-leave-cancel">{{ __('button.cancel') }}</button>
              <button type="button" class="btn btn-primary" data-testid="upload-leave-confirm" @click="confirmLeave">{{ __('video.upload_leave_confirm') }}</button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { traverseDataTransferItems, filesFromInput } from '../dashboard/useDirectoryPicker';
import KindUploadFolderGroupComponent from './KindUploadFolderGroupComponent.vue';
import { resolveUploadPartPlan } from '../lib/uploadPartPlan';

// アップロード対象の登録リクエストを分割送信するチャンクサイズ
const FOLDER_UPLOAD_CHUNK_SIZE = 20;
// S3 への同時アップロード件数 (2026-09-28 の work 実測では、同時 3 件でも所要時間が
// ほぼ変わらなかった (ブラウザのメインスレッドの処理待ちが律速だった)。そのため、
// 利用者環境で効果を確認できるまでは 1 (直列) にしている。値を増やせば並列で送る。
// (hover: none) 端末は 1 件ずつの直列を維持する。
const UPLOAD_CONCURRENCY_PC = 1;
const UPLOAD_CONCURRENCY_MOBILE = 1;
// 添付先「自動」の対象ファイルを決める種別の優先順位
// (サーバ側 VideoController::UPLOAD_PRIORITY と一致させる。ここに無い種別は最後尾扱い)
const UPLOAD_PRIORITY = ['video', 'audio', 'image', 'data', 'document', 'others'];
// 「登録」を押さずに戻るボタンで離れるのを受け止めるために足す履歴の印 (history.state に付ける)
const UPLOAD_GUARD_STATE_KEY = 'exaUploadGuard';

// PHP 側 KindResolver::EXTENSION_MAP と同期必須(ビルドが分離しているため複製を維持している。
// 拡張子マップを変更する場合は必ず両方を同時に更新すること)
const EXT_TO_KIND = {
  // video(10)
  avi: 'video', m4v: 'video', mkv: 'video', mov: 'video', mp4: 'video',
  mpeg: 'video', mpg: 'video', mts: 'video', mxf: 'video', wmv: 'video',
  // audio(6)
  aac: 'audio', flac: 'audio', m4a: 'audio', mp3: 'audio', ogg: 'audio', wav: 'audio',
  // document(5)
  doc: 'document', docx: 'document', md: 'document', pdf: 'document', txt: 'document',
  // data(6)
  csv: 'data', json: 'data', log: 'data', xls: 'data', xlsx: 'data', xml: 'data',
  // presentation(2)
  ppt: 'presentation', pptx: 'presentation',
  // image(10)
  ai: 'image', bmp: 'image', gif: 'image', jpeg: 'image', jpg: 'image',
  png: 'image', psd: 'image', tif: 'image', tiff: 'image', webp: 'image',
  // cad(95)
  dwg: 'cad', dxf: 'cad',
  step: 'cad', stp: 'cad',
  iges: 'cad', igs: 'cad',
  rvt: 'cad', ifc: 'cad',
  nwd: 'cad', nwc: 'cad',
  dwf: 'cad', dwfx: 'cad',
  '3dm': 'cad', '3ds': 'cad', '3dxml': 'cad', a: 'cad',
  asm: 'cad', axm: 'cad', brd: 'cad', catpart: 'cad',
  catproduct: 'cad', cgr: 'cad', collaboration: 'cad', dae: 'cad',
  ddx: 'cad', ddz: 'cad', dgk: 'cad', dgn: 'cad',
  dlv3: 'cad', dmt: 'cad', dwt: 'cad', emodel: 'cad',
  exp: 'cad', f3d: 'cad', fbrd: 'cad', fbx: 'cad',
  fsch: 'cad', g: 'cad', gbxml: 'cad', glb: 'cad',
  gltf: 'cad', iam: 'cad', idw: 'cad', ige: 'cad',
  ipt: 'cad', iwm: 'cad', jt: 'cad', max: 'cad',
  model: 'cad', mpf: 'cad', msr: 'cad', neu: 'cad',
  obj: 'cad', osb: 'cad', par: 'cad', pmlprj: 'cad',
  pmlprjz: 'cad', prt: 'cad', psm: 'cad', psmodel: 'cad',
  rvm: 'cad', sab: 'cad', sat: 'cad', sch: 'cad',
  session: 'cad', skp: 'cad', sldasm: 'cad', sldprt: 'cad',
  smb: 'cad', smt: 'cad', ste: 'cad', stl: 'cad',
  stla: 'cad', stlb: 'cad', stpz: 'cad', usd: 'cad',
  usda: 'cad', usdc: 'cad', usdz: 'cad', vpb: 'cad',
  vue: 'cad', wire: 'cad', x_b: 'cad', x_t: 'cad',
  xas: 'cad', xpr: 'cad',
  '3mf': 'cad', '123dx': 'cad', atfx: 'cad', bdf: 'cad',
  emn: 'cad', pcbdata: 'cad', pcbxml: 'cad', sta: 'cad',
  slddrw: 'cad',
};

// 元ファイル削除チェックボックスの対象拡張子（EXT_TO_KIND とは独立。kind を手動変更しても判定は変わらない）
const SOURCE_DELETE_EXTS = ['mxf', 'mov', 'mts', 'mp4'];
const JUNK_EXACT_MATCH = [
  '.ds_store', 'thumbs.db', 'ehthumbs.db', 'ehthumbs_vista.db', 'desktop.ini',
  '.localized', 'network trash folder', 'temporary items',
];
const JUNK_PREFIX_MATCH = ['._', '.~lock.', '~$', '.fuse_hidden', '.nfs'];
const JUNK_SUFFIX_MATCH = ['.tmp', '.swp', '.swo', '~', '.zone.identifier', '.lnk'];

const isJunkFile = (filename) => {
  const lower = String(filename || '').toLowerCase();
  return JUNK_EXACT_MATCH.includes(lower)
    || JUNK_PREFIX_MATCH.some((pattern) => lower.startsWith(pattern))
    || JUNK_SUFFIX_MATCH.some((pattern) => lower.endsWith(pattern));
};

export default {
  name: 'KindUploadList',
  components: { vSelect, KindUploadFolderGroup: KindUploadFolderGroupComponent },
  emits: ['uploading', 'api-ready', 'items-changed'],
  props: {
    bucket: String,
    pool: String,
    region: String,
    check_upload_endpoint: { type: String, default: '' },
    folder_upload_endpoint: { type: String, default: '' },
    folder_id: { type: [String, Number], default: '' },
    folder_existence_endpoint: { type: String, default: '' },
    storage_hot_id: { type: [String, Number], required: true },
    storage_cold_id: { type: [String, Number], required: true },
    storage_cold_min_bytes: { type: [String, Number], default: 0 },
    kind_labels: { type: Object, default: () => ({}) },
    old_uploads: { type: Array, default: () => [] },
    max_items: { type: Number, default: 0 },
  },
  data() {
    return {
      storageHotId: Number(this.storage_hot_id),
      storageColdId: Number(this.storage_cold_id),
      coldMinBytes: Number(this.storage_cold_min_bytes),
      items: [],
      s3Busy: false,
      uploadSessionTotal: 0,
      uploadSessionDone: 0,
      uploadSessionFailed: 0,
      registering: false,
      registerProgress: { done: 0, total: 0 },
      dragActive: false,
      folderExistenceMap: {},
      folderExistenceSeq: 0,
      junkExcludedPaths: [],
    };
  },
  computed: {
    kindOptions() {
      const labels = this.kind_labels || {};
      return [
        { value: 'video', label: labels.video || '動画' },
        { value: 'audio', label: labels.audio || '音声' },
        { value: 'document', label: labels.document || '文書' },
        { value: 'data', label: labels.data || '表計算・データ' },
        { value: 'presentation', label: labels.presentation || 'プレゼンテーション' },
        { value: 'image', label: labels.image || '画像' },
        { value: 'cad', label: labels.cad || '図面・CAD' },
        { value: 'others', label: labels.others || 'その他' },
      ];
    },
    hasPending() {
      return this.items.some((it) => it.status === 'pending');
    },
    hasFailed() {
      return this.items.some((it) => it.status === 'failed');
    },
    allUploaded() {
      return this.items.length > 0 && this.items.every((it) => it.status === 'success');
    },
    isMobile() {
      return window.matchMedia('(hover: none)').matches;
    },
    busy() {
      return this.s3Busy || this.registering;
    },
    uploadConcurrency() {
      return this.isMobile ? UPLOAD_CONCURRENCY_MOBILE : UPLOAD_CONCURRENCY_PC;
    },
    uploadProgressText() {
      if (!this.s3Busy || this.uploadSessionTotal === 0) return '';
      let text = this.__('video.upload_progress_uploading')
        .replace(':done', String(this.uploadSessionDone))
        .replace(':total', String(this.uploadSessionTotal));
      if (this.uploadSessionFailed > 0) {
        text += this.__('video.upload_progress_failed_suffix')
          .replace(':count', String(this.uploadSessionFailed));
      }
      return text;
    },
    uploadCompletionText() {
      if (this.s3Busy) return '';
      const failedCount = this.items.filter((it) => it.status === 'failed').length;
      if (failedCount === 0) return '';
      const successCount = this.items.filter((it) => it.status === 'success').length;
      return this.__('video.upload_progress_done')
        .replace(':done', String(successCount))
        .replace(':count', String(failedCount));
    },
    registerFailedCount() {
      return this.items.filter((it) => it.register_status === 'register_failed').length;
    },
    failedSummaryText() {
      return this.__('video.folder_upload_failed_summary').replace(':count', String(this.registerFailedCount));
    },
    junkExcludedCount() {
      return this.junkExcludedPaths.length;
    },
    junkExcludedText() {
      return this.__('video.folder_upload_junk_excluded').replace(':count', String(this.junkExcludedCount));
    },
    registeringProgressText() {
      return this.__('video.folder_upload_registering')
        .replace(':done', String(this.registerProgress.done))
        .replace(':total', String(this.registerProgress.total));
    },
    kindLabelMap() {
      const map = {};
      this.kindOptions.forEach((opt) => {
        map[opt.value] = opt.label;
      });
      return map;
    },
    folderItems() {
      return this.items.filter((it) => it.relative_dir);
    },
    normalItems() {
      return this.items.filter((it) => !it.relative_dir);
    },
    totalCount() {
      return this.items.length;
    },
    totalCountText() {
      return this.__('video.folder_upload_total_count').replace(':count', String(this.totalCount));
    },
    // フォルダ経由アイテムの relative_dir からフォルダ階層ツリーを構築する。
    // 兄弟ノードの並び順は「その階層でそのフォルダ名が最初に出現した投入順」。
    // 同じパスは Map で単一ノードに集約するため、後から別フォルダを挟んで
    // 同じ親のアイテムが追加されても、そのサブツリーは分断されず連続して並ぶ。
    folderTree() {
      const rootChildren = [];
      const nodeMap = new Map();
      this.folderItems.forEach((item) => {
        const segments = (item.relative_dir || '').split('/').filter((s) => s !== '');
        if (segments.length === 0) return;
        let siblings = rootChildren;
        let currentPath = '';
        let leafNode = null;
        segments.forEach((seg) => {
          currentPath = currentPath ? `${currentPath}/${seg}` : seg;
          let node = nodeMap.get(currentPath);
          if (!node) {
            node = { path: currentPath, name: seg, items: [], children: [] };
            nodeMap.set(currentPath, node);
            siblings.push(node);
          }
          leafNode = node;
          siblings = node.children;
        });
        leafNode.items.push(item);
      });
      return rootChildren;
    },
    // folderTree の全ノード(中間階層含む)のパスを重複なく集める。
    // 既存/新規判定 API は「渡したパスの最下層まで全部存在するか」を返す仕様のため、
    // relative_dir(=葉ノードのパス)だけでなく中間階層のパスも個別に問い合わせる必要がある。
    folderNodePaths() {
      const paths = [];
      const walk = (nodes) => {
        nodes.forEach((node) => {
          paths.push(node.path);
          if (node.children && node.children.length > 0) {
            walk(node.children);
          }
        });
      };
      walk(this.folderTree);
      return paths;
    },
  },
  watch: {
    folder_id() {
      this.refreshFolderExistence();
    },
    allUploaded: {
      immediate: true,
      handler(ready) {
        // 全件アップロード済みのときだけ「登録する」を有効化する
        this.$emit('uploading', !ready);
      },
    },
    items: {
      deep: true,
      immediate: true,
      handler() {
        // S3 アップロード完了済み(通常アイテム/フォルダ経由アイテム問わず)のみ、
        // 添付先ファイル選択肢として親コンポーネント(app.js)へ公開する
        const successItems = this.items.filter((it) => it.status === 'success' && it.filename);

        // 添付先「自動」を選んだときに実際に添付される1件を判定する。
        // サーバ側 VideoController::pickParentUpload と同じルール(UPLOAD_PRIORITY の優先度が
        // 最小のもの。indexOf(-1)は最後尾扱い)で、全ファイル(フォルダ経由・通常問わず)の中から
        // 選ぶ。reduce は先頭から走査し「priorityOf(cur) < priorityOf(best)」の時だけ入れ替える
        // ため、同順位なら successItems 内で先に現れたもの(=投入順が早いもの)が残る。
        // 全ファイルを対象にしたことで、従来「通常アイテムがあれば通常アイテムだけから選ぶ」
        // としていた優先ルールは廃止し、種別だけで選ぶ(フォルダ経由か否かは見ない)。
        const autoCandidates = successItems.filter((it) => it.register_status !== 'registered');
        let autoTargetItem = null;
        if (autoCandidates.length > 0) {
          const priorityOf = (it) => {
            const idx = UPLOAD_PRIORITY.indexOf(it.kind);
            return idx === -1 ? UPLOAD_PRIORITY.length : idx;
          };
          autoTargetItem = autoCandidates.reduce((best, cur) => (
            priorityOf(cur) < priorityOf(best) ? cur : best
          ));
        }

        const targets = successItems.map((it) => {
          const kindLabel = (it.kind === 'others' && it.kind_label) ? it.kind_label : this.kindLabelOf(it.kind);
          // 表示ラベルが一意になる根拠: acceptEntries() の重複ガードにより
          // 「相対パス+ファイル名+サイズ」が完全一致するアイテムは追加できないため、
          // この3要素(relative_dir/original_name/formatSize)を含む表示は必ず区別できる。
          const displayLabel = it.relative_dir
            ? `${it.relative_dir}/${it.original_name}（${kindLabel}・${this.formatSize(it.filesize)}）`
            : `${it.original_name}（${kindLabel}・${this.formatSize(it.filesize)}）`;
          // display_path は「自動」選択肢に実ファイル名を表示するための簡潔表記(パス+ファイル名のみ)
          const displayPath = it.relative_dir
            ? `${it.relative_dir}/${it.original_name}`
            : it.original_name;
          return {
            filename: it.filename,
            original_name: it.original_name,
            kind: it.kind,
            kind_label: kindLabel,
            is_folder_item: !!it.relative_dir,
            display_label: displayLabel,
            display_path: displayPath,
            // 添付を S3 に送る前の新規アップロード量の確認 (MultiUploadFormComponent) に使う
            filesize: Number(it.filesize) || 0,
            storage_id: this.isPlacedCold(it) ? this.storageColdId : this.storageHotId,
            is_auto_target: autoTargetItem ? it === autoTargetItem : false,
          };
        });
        this.$emit('items-changed', targets);
      },
    },
  },
  created() {
    if (Array.isArray(this.old_uploads) && this.old_uploads.length > 0) {
      this.items = this.old_uploads.map((u) => this.makeItem({
        kind: u.kind || null,
        kind_label: u.kind_label || '',
        filename: u.filename || '',
        filesize: Number(u.filesize) || 0,
        original_name: u.original_name || '',
        source_updated_at: u.source_updated_at || '',
        upload_expires_at: u.upload_expires_at || '',
        upload_token: u.upload_token || '',
        storage_id: Number(u.storage_id) || this.storageHotId,
        delete_source: u.delete_source,
        // filename を持つ = 既に S3 へアップロード済み（バリデーションエラー後の再描画）
        status: u.filename ? 'success' : 'pending',
      }));
    }
  },
  mounted() {
    this.s3Client = new S3Client({
      region: this.region,
      credentials: fromCognitoIdentityPool({
        clientConfig: { region: this.region },
        identityPoolId: this.pool,
      }),
      requestChecksumCalculation: 'WHEN_REQUIRED',
    });

    // 親コンポーネント(app.js)へ登録処理を公開する
    this.$emit('api-ready', {
      itemCount: () => this.items.length,
      registerFolderItems: this.registerFolderItems.bind(this),
    });

    // 「登録」を押さずに離れるときの確認 (戻る・画面内のリンク・タブを閉じる/再読み込み)
    this.leaving = false;
    this.leaveSelfBack = false;
    this.leaveTarget = null;
    this.leaveConfirmed = false;
    window.addEventListener('popstate', this.onLeavePopState);
    window.addEventListener('click', this.onLeaveLinkClick, true);
    window.addEventListener('beforeunload', this.onLeaveBeforeUnload);
    if (window.jQuery && this.$refs.leaveModal) {
      window.jQuery(this.$refs.leaveModal).on('hide.bs.modal', this.onLeaveDialogHide);
    }
  },
  beforeUnmount() {
    window.removeEventListener('popstate', this.onLeavePopState);
    window.removeEventListener('click', this.onLeaveLinkClick, true);
    window.removeEventListener('beforeunload', this.onLeaveBeforeUnload);
    if (window.jQuery && this.$refs.leaveModal) {
      const $modal = window.jQuery(this.$refs.leaveModal);
      $modal.off('hide.bs.modal', this.onLeaveDialogHide);
      if (this.leaveTarget) {
        this.leaveTarget = null;
        this.leaveConfirmed = true;
        $modal.modal('hide');
      }
    }
  },
  methods: {
    // 未登録のファイルが 1 件でもあるか (一覧が空なら false)。確認を出す瞬間に this.items を見る
    hasUnregisteredItems() {
      return this.items.some((it) => it.register_status !== 'registered');
    },
    isLeaveGuardEntry() {
      return !!(window.history.state && window.history.state[UPLOAD_GUARD_STATE_KEY]);
    },
    // 戻るの受け止め用に、同じ URL の履歴を 1 件足す (今の state を残して印を足す)。利用者の操作の中で呼ぶ
    pushLeaveGuardEntry() {
      if (this.isLeaveGuardEntry()) return;
      const base = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
      window.history.pushState({ ...base, [UPLOAD_GUARD_STATE_KEY]: true }, '', window.location.href);
    },
    // 未登録が無くなったら、足した履歴から降りる (戻るが 1 回で効くように)
    releaseLeaveGuardEntry() {
      if (this.leaving || this.hasUnregisteredItems() || !this.isLeaveGuardEntry()) return;
      this.leaveSelfBack = true;
      window.history.back();
    },
    canShowLeaveDialog() {
      return !!(window.jQuery && this.$refs.leaveModal);
    },
    showLeaveDialog(target) {
      this.leaveTarget = target;
      this.leaveConfirmed = false;
      window.jQuery(this.$refs.leaveModal).modal('show');
    },
    // ブラウザの戻る: 足した履歴から印の無い履歴へ降りてきたとき
    onLeavePopState() {
      if (this.leaving) return;
      if (this.leaveSelfBack) {
        this.leaveSelfBack = false;
        const target = this.leaveTarget;
        if (target && target.type === 'link') {
          this.leaveTarget = null;
          this.leaving = true;
          target.link.click();
        }
        return;
      }
      if (this.isLeaveGuardEntry() || !this.hasUnregisteredItems() || !this.canShowLeaveDialog()) return;
      this.showLeaveDialog({ type: 'back' });
    },
    // 画面内のリンク: 同じタブで移るリンクのクリックを先に受け取って止める
    onLeaveLinkClick(e) {
      if (this.leaving || !e.target || typeof e.target.closest !== 'function') return;
      if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      if (e.target.closest('#uploadLeaveModal')) return;
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = (link.getAttribute('href') || '').trim().toLowerCase();
      if (href === '' || href.startsWith('#') || href.startsWith('javascript:')) return;
      if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;
      if (!this.hasUnregisteredItems() || !this.canShowLeaveDialog()) return;
      // preventDefault() だけでは、ログアウトのリンクの inline onclick がフォームを送ってしまう
      e.preventDefault();
      e.stopPropagation();
      this.showLeaveDialog({ type: 'link', link });
    },
    // タブを閉じる・再読み込み: 文はブラウザが決める
    onLeaveBeforeUnload(e) {
      if (this.leaving || !this.hasUnregisteredItems()) return;
      e.preventDefault();
      e.returnValue = '';
    },
    // 「移動」
    confirmLeave() {
      const target = this.leaveTarget;
      if (!target) return;
      this.leaveConfirmed = true;
      window.jQuery(this.$refs.leaveModal).modal('hide');
      if (target.type === 'back') {
        // 足した履歴の 1 つ下に降りている。もう 1 つ戻って開く前の画面へ移る
        this.leaveTarget = null;
        this.leaving = true;
        window.history.back();
        return;
      }
      if (this.isLeaveGuardEntry()) {
        // 足した履歴から降りた後に、同じリンクをもう一度押す (popstate で続きを行う)
        this.leaveSelfBack = true;
        window.history.back();
        return;
      }
      this.leaveTarget = null;
      this.leaving = true;
      target.link.click();
    },
    // ダイアログが閉じるとき。「移動」以外 (キャンセル・×・Esc・外側のクリック) は画面に残る
    onLeaveDialogHide() {
      if (this.leaveConfirmed) return;
      this.leaveTarget = null;
      // この閉じる操作の中 (利用者の操作) で、受け止め用の履歴を足し直す
      if (this.hasUnregisteredItems()) this.pushLeaveGuardEntry();
    },
    generateUuid() {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
      return 'id-' + Math.random().toString(36).slice(2) + Date.now();
    },
    makeItem(init = {}) {
      const kind = init.kind || null;
      return {
        _uuid: this.generateUuid(),
        _file: init._file || null,
        _kindSelection: kind ? this.kindOptions.find((o) => o.value === kind) || { value: kind, label: kind } : null,
        kind,
        kind_label: init.kind_label || '',
        filename: init.filename || '',
        filesize: init.filesize || 0,
        original_name: init.original_name || '',
        extension: init.extension || '',
        source_updated_at: init.source_updated_at || '',
        upload_expires_at: init.upload_expires_at || '',
        upload_token: init.upload_token || '',
        storage_id: (init.delete_source === true || init.delete_source === '1')
          ? this.storageHotId
          : (Number(init.storage_id) || this.storageHotId),
        delete_source: init.delete_source === true || init.delete_source === '1',
        status: init.status || 'pending',
        percent: 0,
        error: init.error || '',
        // フォルダ経由アイテムかどうか(空文字=通常アイテム)
        relative_dir: init.relative_dir || '',
        // サーバへの登録処理ステータス(S3 アップロード用の status とは独立管理)
        register_status: init.register_status || '',
        register_error: '',
        video_id: init.video_id || null,
      };
    },
    extractExtension(name) {
      const m = (name || '').match(/(.*)(?:\.([^.]+$))/);
      return m ? m[2] : '';
    },
    isSourceDeleteEligible(item) {
      const ext = this.extractExtension(item.original_name).toLowerCase();
      return SOURCE_DELETE_EXTS.includes(ext);
    },
    formatSize(bytes) {
      const n = Number(bytes) || 0;
      if (n < 1024) return `${n} B`;
      if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
      if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
      return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
    },
    statusLabel(status) {
      const map = {
        pending: this.__('video.upload_status_pending'),
        uploading: this.__('video.upload_status_uploading'),
        success: this.__('video.upload_status_success'),
        failed: this.__('video.upload_status_failed'),
      };
      return map[status] || '';
    },
    statusIcon(status) {
      const map = {
        pending: 'fa-regular fa-clock',
        uploading: 'fa-solid fa-spinner fa-spin',
        success: 'fa-solid fa-circle-check',
        failed: 'fa-solid fa-circle-xmark',
      };
      return map[status] || '';
    },
    statusClass(status) {
      const map = {
        pending: 'text-muted',
        uploading: 'text-primary',
        success: 'text-success',
        failed: 'text-danger',
      };
      return map[status] || '';
    },
    kindLabelOf(kind) {
      const opt = this.kindOptions.find((o) => o.value === kind);
      return opt ? opt.label : (kind || '');
    },
    registerStatusLabel(status) {
      const map = {
        registering: this.__('video.folder_upload_status_registering'),
        registered: this.__('video.folder_upload_register_done'),
        register_failed: this.__('video.folder_upload_register_failed'),
      };
      return map[status] || '';
    },
    registerStatusIcon(status) {
      const map = {
        registering: 'fa-solid fa-spinner fa-spin',
        registered: 'fa-solid fa-circle-check',
        register_failed: 'fa-solid fa-circle-xmark',
      };
      return map[status] || '';
    },
    registerStatusClass(status) {
      const map = {
        registering: 'text-primary',
        registered: 'text-success',
        register_failed: 'text-danger',
      };
      return map[status] || '';
    },
    onDragActive(active) {
      if (this.busy) return;
      this.dragActive = active;
    },
    openFileDialog() {
      if (this.busy) return;
      this.$refs.fileInput.click();
    },
    openDirDialog() {
      if (this.busy) return;
      this.$refs.dirInput.click();
    },
    onDropzoneClick(e) {
      if (e.target.closest('button')) return;
      this.openFileDialog();
    },
    async onDrop(e) {
      this.dragActive = false;
      if (this.busy) return;
      const items = e.dataTransfer && e.dataTransfer.items;
      let entries = [];
      if (items && items.length > 0 && typeof items[0].webkitGetAsEntry === 'function') {
        entries = await traverseDataTransferItems(items);
      } else if (e.dataTransfer && e.dataTransfer.files) {
        entries = filesFromInput(e.dataTransfer.files, false);
      }
      this.acceptEntries(entries);
    },
    onFileInputChange(e) {
      this.acceptEntries(filesFromInput(e.target.files, false));
      e.target.value = '';
    },
    onDirInputChange(e) {
      this.acceptEntries(filesFromInput(e.target.files, true));
      e.target.value = '';
    },
    acceptFiles(fileList) {
      this.acceptEntries(filesFromInput(fileList, false));
    },
    acceptEntries(entries) {
      if (this.busy) return;
      const incoming = Array.from(entries || []);
      if (incoming.length === 0) return;

      const list = [];
      incoming.forEach((entry) => {
        if (! entry.file) return;
        if (! isJunkFile(entry.file.name)) {
          list.push(entry);
          return;
        }
        // 除外したファイルは、どれが除外されたのか分かるように表示パスを覚えておく。
        // 同じフォルダを選び直しても増えないよう、パスが同じものは積まない。
        const dir = entry.sourcePath ? entry.sourcePath.replace(/^\/+|\/+$/g, '') : '';
        const path = dir ? `${dir}/${entry.file.name}` : entry.file.name;
        if (! this.junkExcludedPaths.includes(path)) {
          this.junkExcludedPaths.push(path);
        }
      });
      if (list.length === 0) return;

      // 件数上限チェック(全ファイル対象)
      const limit = this.max_items;
      if (limit > 0) {
        const totalAfterAdd = this.items.length + list.length;
        if (totalAfterAdd > limit) {
          window.alert(this.__('video.upload_max_items_exceeded').replace(':max', String(limit)));
          return;
        }
      }

      let addedFolderItem = false;
      for (const entry of list) {
        const file = entry.file;
        if (!file) continue;
        const relativeDir = entry.sourcePath ? entry.sourcePath.replace(/^\/+|\/+$/g, '') : '';

        // 相対パス+ファイル名+サイズが同一の重複は無視(別サブフォルダの同名ファイルは区別する)
        const dupKey = (relativeDir || '') + '/' + file.name + '\0' + file.size;
        const dup = this.items.some((it) => {
          const key = (it.relative_dir || '') + '/' + it.original_name + '\0' + it.filesize;
          return key === dupKey;
        });
        if (dup) continue;

        const ext = this.extractExtension(file.name).toLowerCase();
        const detectedKind = EXT_TO_KIND[ext] || 'others';
        this.items.push(this.makeItem({
          _file: file,
          kind: detectedKind,
          filesize: file.size,
          original_name: file.name,
          extension: ext,
          status: 'pending',
          relative_dir: relativeDir,
        }));
        if (relativeDir) {
          addedFolderItem = true;
        }
      }

      if (addedFolderItem) {
        this.refreshFolderExistence();
      }
      if (this.hasUnregisteredItems()) this.pushLeaveGuardEntry();
    },
    // フォルダ経由アイテムのツリー内の各パスが necfru 側に既存フォルダとして存在するかを
    // サーバへ問い合わせ、folderExistenceMap を更新する。判定はあくまで補助表示のため、
    // 失敗時はエラーを出さずバッジを消すだけにとどめる(登録処理はブロックしない)。
    async refreshFolderExistence() {
      if (!this.folder_existence_endpoint) return;

      const isRoot = this.folder_id === 'root';
      const folderId = isRoot ? null : Number(this.folder_id);
      const paths = this.folderNodePaths;
      if ((!isRoot && !folderId) || paths.length === 0) {
        this.folderExistenceMap = {};
        return;
      }

      // 後発リクエストの結果だけを採用する (保存先フォルダ連続変更時に古い応答が
      // 後着して上書きするのを防ぐ)
      const seq = ++this.folderExistenceSeq;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const requestBody = isRoot ? { to_root: 1, paths } : { folder_id: folderId, paths };

      try {
        const res = await fetch(this.folder_existence_endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfToken,
          },
          body: JSON.stringify(requestBody),
        });
        if (seq !== this.folderExistenceSeq) return;   // 古い応答は捨てる
        if (!res.ok) {
          this.folderExistenceMap = {};                 // 判定できないのでバッジを消す
          return;
        }
        const body = await res.json();
        if (seq !== this.folderExistenceSeq) return;
        this.folderExistenceMap = body.existence || {};
      } catch (e) {
        if (seq === this.folderExistenceSeq) {
          this.folderExistenceMap = {};
        }
      }
    },
    removeItem(item) {
      if (this.busy) return;
      // 取り除けるのは次の 3 つ。
      // - pending: S3 へまだアップロードしていない行
      // - failed: uploadOne() が例外を投げて中断した行 (アップロード自体が失敗しているため
      //   S3 に完了オブジェクトは作られていない)
      // - register_failed: S3 へのアップロードは終わったが、サーバーへの登録に失敗した行。
      //   取り除くと S3 のオブジェクトはファイルとして登録されないまま残る
      if (!item) return;
      const removable = item.status === 'pending'
        || item.status === 'failed'
        || item.register_status === 'register_failed';
      if (!removable) return;
      const idx = this.items.indexOf(item);
      if (idx === -1) return;
      // 削除でノードが消えても folderExistenceMap は再取得しない: 削除はツリーからパスを
      // 減らすだけで新しいパスを増やさないため、消えたノードの判定結果が map に残っていても
      // 描画対象外(参照されない)になるだけで表示上は無害。無駄な API 呼び出しを避ける。
      this.items.splice(idx, 1);
      this.releaseLeaveGuardEntry();
    },
    onKindSelected(item, opt) {
      if (!opt || !item) return;
      item.kind = opt.value;
    },
    isColdDisabled(item) {
      if (!item) return false;
      return this.coldMinBytes > 0 && item.filesize > 0 && item.filesize < this.coldMinBytes;
    },
    // 元ファイル削除が効くか。サーバー (VideoController::resolveUploadStorageId) と同じく、対象拡張子のときだけ有効
    isSourceDeleteApplied(item) {
      if (!item) return false;
      return !!item.delete_source && this.isSourceDeleteEligible(item);
    },
    // COLD が選ばれ、実際に COLD に置かれるファイルか (小さいファイル・元ファイル削除 (対象拡張子) は COLD を選んでも HOT)
    isPlacedCold(item) {
      if (!item) return false;
      return Number(item.storage_id) === Number(this.storageColdId) && !this.isColdDisabled(item) && !this.isSourceDeleteApplied(item);
    },
    onDeleteSourceChange(item) {
      if (!item) return;
      if (item.delete_source) {
        item.storage_id = this.storageHotId;
      }
    },
    bulkStorage(targetId) {
      if (this.busy) return;
      const target = Number(targetId);
      this.items.forEach((item) => {
        // コールド一括時、サイズ下限未満のファイル・元ファイル削除対象はホットのまま据え置く
        if (target === this.storageColdId && (this.isColdDisabled(item) || item.delete_source)) {
          item.storage_id = this.storageHotId;
          return;
        }
        item.storage_id = target;
      });
    },
    async uploadAll(mode) {
      if (this.busy) return;
      // 「失敗分と未送信分をアップロード」は失敗行と未送信行をまとめて送る。
      // 「アップロード」(mode: 'pending') は未送信行だけを送る現行のまま。
      const targets = mode === 'failed'
        ? this.items.filter((it) => it.status === 'failed' || it.status === 'pending')
        : this.items.filter((it) => it.status === mode);
      if (targets.length === 0) return;

      const folderTargets = targets.filter((it) => it.relative_dir);
      if (folderTargets.length > 0) {
        try {
          await this.checkFolderUploadAllowed(folderTargets);
        } catch (err) {
          window.alert((err && err.message) ? err.message : this.__('video.upload_failed_generic'));
          return;
        }
      }

      // 添付つきのとき、添付先になる本体をこの回で S3 に送るなら、S3 に送る前に
      // 本体 + 添付の合計 × 2 で新規アップロード量を確認する (止まったら本体も添付も S3 に送らない)
      try {
        await this.checkAttachmentUploadAllowed(targets);
      } catch (err) {
        window.alert((err && err.message) ? err.message : this.__('video.upload_failed_generic'));
        return;
      }

      this.s3Busy = true;
      this.uploadSessionTotal = targets.length;
      this.uploadSessionDone = 0;
      this.uploadSessionFailed = 0;
      try {
        // 同時 uploadConcurrency 件までワーカーを走らせ、1 件終わるごとに次を始める。
        // item 単位で catch しているため、1 件の失敗が他の送信を止めない。
        const queue = [...targets];
        const runWorker = async () => {
          let item = queue.shift();
          while (item) {
            item.status = 'uploading';
            item.percent = 0;
            item.error = '';
            try {
              await this.uploadOne(item);
              item.status = 'success';
              item.percent = 100;
              this.uploadSessionDone += 1;
            } catch (err) {
              item.status = 'failed';
              item.error = (err && err.message) ? err.message : this.__('video.upload_failed_generic');
              this.uploadSessionFailed += 1;
            }
            item = queue.shift();
          }
        };
        const workerCount = Math.min(this.uploadConcurrency, targets.length);
        await Promise.all(Array.from({ length: workerCount }, () => runWorker()));
      } finally {
        this.s3Busy = false;
      }
    },
    async checkUploadAllowed(file, item) {
      if (!this.check_upload_endpoint) {
        throw new Error(this.__('video.upload_failed_generic'));
      }
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const res = await fetch(this.check_upload_endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': csrfToken,
        },
        // storage_id / delete_source: 置き先 (HOT/COLD)。サーバーは HOT/COLD 容量の上限をこの置き先で判定する
        // (元ファイル削除が対象拡張子で効くと HOT)
        body: JSON.stringify({
          filesize: file.size,
          original_name: file.name,
          storage_id: item ? item.storage_id : undefined,
          delete_source: item ? this.isSourceDeleteApplied(item) : undefined,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || this.__('video.upload_failed_generic'));
      }
      return res.json();
    },
    // 添付の合計 (画面の添付の hidden の attachments_{n}_filesize の合計)。添付が無ければ 0
    attachmentTotalBytes() {
      let total = 0;
      document.querySelectorAll('input[name^="attachments_"][name$="_filesize"]').forEach((el) => {
        if (/^attachments_\d+_filesize$/.test(el.name)) {
          total += Number(el.value) || 0;
        }
      });
      return total;
    },
    // 添付が付く本体のうち、この回で S3 に送るもの。添付先が送信済みの本体を明示で選んでいるときは null
    // (添付の確認は添付を S3 に送る側が行う)。「自動」は app.js の添付先の決め方
    // (items-changed の is_auto_target と同じ種別の優先度) に合わせる
    attachmentTargetInUpload(targets) {
      const select = document.getElementById('attachments_target_select');
      if (select && select.value !== '') return null;
      const candidates = this.items.filter((it) => (
        (it.status === 'success' && it.filename && it.register_status !== 'registered') || targets.includes(it)
      ));
      if (candidates.length === 0) return null;
      const priorityOf = (it) => {
        const idx = UPLOAD_PRIORITY.indexOf(it.kind);
        return idx === -1 ? UPLOAD_PRIORITY.length : idx;
      };
      const auto = candidates.reduce((best, cur) => (priorityOf(cur) < priorityOf(best) ? cur : best));
      return targets.includes(auto) ? auto : null;
    },
    async checkAttachmentUploadAllowed(targets) {
      if (!this.check_upload_endpoint) return;
      const attachmentBytes = this.attachmentTotalBytes();
      if (attachmentBytes <= 0) return;
      const target = this.attachmentTargetInUpload(targets);
      if (!target) return;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const res = await fetch(this.check_upload_endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          filesize: target.filesize,
          storage_id: target.storage_id,
          delete_source: this.isSourceDeleteApplied(target),
          attachment_filesize: attachmentBytes,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || this.__('video.upload_failed_generic'));
      }
    },
    async checkFolderUploadAllowed(items) {
      if (!this.check_upload_endpoint) return;
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
      const totalFilesize = items.reduce((sum, item) => sum + Number(item.filesize || 0), 0);
      // HOT に置かれる合計と COLD に置かれる合計を別々に送る (HOT/COLD 容量の上限を置き先ごとに判定するため)
      const coldFilesize = items.reduce((sum, item) => sum + (this.isPlacedCold(item) ? Number(item.filesize || 0) : 0), 0);
      const hotFilesize = totalFilesize - coldFilesize;
      const res = await fetch(this.check_upload_endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': csrfToken,
        },
        body: JSON.stringify({
          file_count: items.length,
          total_filesize: totalFilesize,
          hot_filesize: hotFilesize,
          cold_filesize: coldFilesize,
        }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || this.__('video.upload_failed_generic'));
      }
    },
    async uploadOne(item) {
      const file = item._file;
      if (!file) {
        throw new Error(this.__('video.upload_failed_generic'));
      }
      const prepared = await this.checkUploadAllowed(file, item);
      const objectKey = prepared.key;
      const params = {
        Bucket: this.bucket,
        Key: objectKey,
        ContentType: file.type,
        ContentDisposition: 'attachment',
        Body: file,
        ACL: 'private',
        Tagging: 'status=pending',
      };
      const { partSize, queueSize } = this.isMobile
        ? resolveUploadPartPlan(file.size, 5 * 1024 * 1024, 3)
        : resolveUploadPartPlan(file.size, 20 * 1024 * 1024, 20);
      const upload = new Upload({
        client: this.s3Client,
        params,
        partSize,
        queueSize,
      });
      upload.on('httpUploadProgress', (evt) => {
        item.percent = parseInt((evt.loaded * 100) / evt.total, 10);
      });
      await upload.done();

      item.filename = objectKey;
      item.filesize = file.size;
      item.original_name = file.name;
      item.extension = this.extractExtension(file.name);
      item.source_updated_at = file.lastModified
        ? new Date(file.lastModified).toISOString().slice(0, 19).replace('T', ' ')
        : '';
      item.upload_expires_at = prepared.expires_at;
      item.upload_token = prepared.upload_token;
    },
    // アップロード済みのファイル(S3 アップロード済み・未登録のもの)を、
    // 20 件ずつチャンクに分けてサーバへ登録する。親コンポーネント(app.js)から
    // フォーム送信時に呼び出される。
    // attachmentsTargetFilename: 添付ファイルの添付先として選択された filename。
    // 非 null の場合のみ、それを含むチャンクに添付ファイル情報を同梱する。
    async registerFolderItems(formEl, attachmentsTargetFilename = null) {
      const targets = this.items.filter((it) => it.status === 'success'
        && it.register_status !== 'registered');
      if (targets.length === 0) {
        return { ok: true, created: 0, attachments_error: null };
      }

      const fd = new FormData(formEl);
      if (this.folder_id === '') {
        window.alert(this.__('video.folder_upload_folder_required'));
        return { ok: false };
      }
      const tags = fd.getAll('tags[]').map(Number);
      // ai_analysis_requested は hidden(未チェック用の "0") + checkbox(チェック時 "1") の
      // 同名 2 要素構成のため、FormData.get ではなく checkbox 要素の checked を直接見る。
      const aiCheckbox = formEl.querySelector('input[type="checkbox"][name="ai_analysis_requested"]');
      const baseBody = this.folder_id === 'root'
        ? { to_root: 1, tags }
        : { folder_id: Number(this.folder_id), tags };
      if (aiCheckbox) {
        baseBody.ai_analysis_requested = aiCheckbox.checked;
      }

      // 概要(descriptions[N][title|body|sort_order])は、このリクエストで新規作成される
      // フォルダに複製される可能性があるため、どのチャンクが新規フォルダを作るか事前に
      // 分からない。よって全チャンクに同梱する。
      const descriptionMap = {};
      for (const [key, value] of fd.entries()) {
        const m = key.match(/^descriptions\[(\d+)\]\[(title|body|sort_order)\]$/);
        if (!m) continue;
        const idx = Number(m[1]);
        if (!descriptionMap[idx]) descriptionMap[idx] = {};
        descriptionMap[idx][m[2]] = value;
      }
      const descriptions = Object.keys(descriptionMap)
        .map(Number)
        .sort((a, b) => a - b)
        .map((idx) => descriptionMap[idx])
        .filter((d) => (d.title || '').trim() !== '' || (d.body || '').trim() !== '')
        .map((d) => ({
          title: d.title || '',
          body: d.body || '',
          sort_order: Number(d.sort_order) || 0,
        }));
      if (descriptions.length > 0) {
        baseBody.descriptions = descriptions;
      }

      // 添付ファイル(attachments_{n}_filename 等)は「対象チャンクのみ」に同梱する。
      // 対象チャンクの判定は各チャンク送信時に行うため、ここではキー収集のみ行う。
      // FormData に添付関連のキーがあれば常に収集する(添付先が「自動」でも同梱できるようにする)。
      let attachmentFields = {};
      for (const [key, value] of fd.entries()) {
        if (/^attachments_\d+_(filename|filesize|original_name|extension)$/.test(key)) {
          attachmentFields[key] = value;
        }
      }
      if (Object.keys(attachmentFields).length === 0) {
        attachmentFields = null;
      }

      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

      this.registering = true;
      this.registerProgress = { done: 0, total: targets.length };
      let aborted = false;
      let attachmentsError = null;

      try {
        for (let i = 0; i < targets.length; i += FOLDER_UPLOAD_CHUNK_SIZE) {
          const chunk = targets.slice(i, i + FOLDER_UPLOAD_CHUNK_SIZE);
          chunk.forEach((it) => {
            it.register_status = 'registering';
            it.register_error = '';
          });

          const requestBody = {
            ...baseBody,
            files: chunk.map((it) => ({
              client_uuid: it._uuid,
              relative_dir: it.relative_dir || '',
              filename: it.filename,
              filesize: it.filesize,
              original_name: it.original_name,
              kind: it.kind,
              kind_label: it.kind_label || null,
              storage_id: it.storage_id,
              source_updated_at: it.source_updated_at || null,
              delete_source: !!it.delete_source,
              upload_expires_at: it.upload_expires_at,
              upload_token: it.upload_token,
            })),
          };
          // 添付先ファイルがこのチャンクに含まれる場合のみ、添付ファイル情報を同梱する。
          // 他のチャンクには含めない(サーバー側も対象外チャンクでは保存しないため無駄な送信を避ける)。
          if (attachmentFields && attachmentsTargetFilename && chunk.some((it) => it.filename === attachmentsTargetFilename)) {
            Object.assign(requestBody, attachmentFields, {
              attachments_target_filename: attachmentsTargetFilename,
            });
          }

          let res = null;
          try {
            res = await fetch(this.folder_upload_endpoint, {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': csrfToken,
              },
              body: JSON.stringify(requestBody),
            });
          } catch (e) {
            res = null;
          }

          if (!res || !res.ok) {
            // このチャンク以降は未送信状態に戻す(以降のチャンクはそもそも未着手のまま)
            chunk.forEach((it) => {
              it.register_status = '';
            });
            let message = this.__('video.folder_upload_generic_error');
            if (res && res.status === 419) {
              message = this.__('video.folder_upload_session_expired');
            } else if (res) {
              // サーバーが理由を返している場合 (検証エラー等) はそれを表示する。
              // 一律の定型文だけだと、何が原因で登録できなかったのか画面から分からなくなる
              const payload = await res.json().catch(() => null);
              const firstError = payload && payload.errors
                ? Object.values(payload.errors).flat()[0]
                : null;
              message = firstError || (payload && payload.message) || message;
            }
            window.alert(message);
            aborted = true;
            break;
          }

          const body = await res.json().catch(() => ({}));
          if (body.attachments_error) {
            attachmentsError = body.attachments_error;
          }
          const resultsByUuid = {};
          (body.results || []).forEach((r) => {
            resultsByUuid[r.client_uuid] = r;
          });

          chunk.forEach((it) => {
            const r = resultsByUuid[it._uuid];
            if (!r) {
              it.register_status = 'register_failed';
              it.register_error = this.__('video.folder_upload_generic_error');
              return;
            }
            if (r.status === 'created') {
              it.register_status = 'registered';
              it.video_id = r.video_id || null;
            } else if (r.status === 'skipped') {
              // 再送時に既に登録済みだったもの(冪等ガード)は登録済み扱いにする
              it.register_status = 'registered';
            } else {
              it.register_status = 'register_failed';
              it.register_error = r.message || this.__('video.folder_upload_item_failed');
            }
          });

          this.registerProgress.done = Math.min(
            this.registerProgress.total,
            this.registerProgress.done + chunk.length
          );
        }
      } finally {
        this.registering = false;
      }

      if (aborted) {
        return { ok: false, attachments_error: attachmentsError };
      }
      // 添付先エラーは、全チャンクの送信が終わってから1回だけ表示する
      // (チャンク単位で出すと同じ内容の alert が連続する)。
      if (attachmentsError) {
        window.alert(attachmentsError);
      }
      const stillFailed = this.items.some((it) => it.register_status === 'register_failed');
      const createdCount = this.items.filter((it) => it.register_status === 'registered').length;
      return { ok: !stillFailed, created: createdCount, attachments_error: attachmentsError };
    },
  },
};
</script>
